import type { IFilesystem } from '@livx.cc/wcli/core';
import { CommandExecutor, registerHeadlessCommands } from '@livx.cc/wcli/core';
import type { Tool, ChatLike } from './llm';
import { imageRefResult } from './llm';
import { forComponent } from './logging';
import { grepTool, globTool, writeTool, multiEditTool, applyEditsTool, repoMapTool, reviewTool, fuzzyLineReplace } from './tools.structured';
import { todoWriteTool, type TodoItem } from './todo';
import { webFetchTool, webSearchTool, webSearchAnthropicTool } from './tools.web';
import { OverlayFilesystem } from './OverlayFilesystem';
import { redactSecrets, CONFIG_FILE_RE } from './redact';
import type { JobRegistry } from './tools.jobs';

const log = forComponent('tools');

/** A structured multiple-choice question the model can pose to a human. */
export interface UserQuestion {
  question: string;
  header?: string;
  options: { label: string; description?: string }[];
  multiSelect?: boolean;
}

/**
 * The host / human-in-the-loop seam (the "third seam" beyond LLM + filesystem).
 * Injected per host: a CLI reads stdin, a browser renders a dialog, edge/headless
 * omits it. Unifies user-questions, permission/plan approvals, and notifications.
 */
export type HostEvent =
  | { kind: 'text_delta'; message: string }
  | { kind: 'speak_utterance'; message: string }
  | { kind: 'thinking_delta'; message: string }
  | { kind: 'tool_use'; id: string; name: string; input: unknown }
  | { kind: 'tool_result'; id: string; output: string; isError?: boolean }
  | { kind: 'tool_result_image'; id: string; dataUrl: string }
  // Display-only tool activity from a self-executing backend (e.g. Cursor CLI). NOT dispatched by the
  // Agent — purely for host UI (rich hosts read name/input/status for cards; plain hosts print `message`).
  | { kind: 'tool_activity'; id?: string; name: string; input?: unknown; output?: unknown; status: 'running' | 'completed' | 'error'; truncated?: { args?: boolean; result?: boolean }; message: string }
  | { kind: string; message: string; data?: unknown };

export interface HostBridge {
  /** Ask the user a structured question; resolve to the chosen label(s) / free text. */
  ask?(q: UserQuestion): Promise<string>;
  /** Request approval for a sensitive action (permission 'ask' / plan approval). */
  confirm?(prompt: string, meta?: { tool: string; input: unknown }): Promise<boolean>;
  /** Emit a progress / notification event to the host UI (non-blocking). */
  notify?(event: HostEvent): void;
}

export interface ToolContext {
  fs: IFilesystem;
  exec: CommandExecutor;
  /** path -> content snapshot at last Read/Edit; powers the read-before-edit staleness guard. */
  readState: Map<string, string>;
  /** optional host interaction channel; absent => autonomous/headless. */
  host?: HostBridge;
  /** optional run-cancellation signal (mirrors AgentOptions.signal); lets abort-aware tools
   *  (e.g. the real shell) kill in-flight work when the run is cancelled. */
  signal?: AbortSignal;
  /** the agent's working todo list (TodoWrite planning aid); replaced wholesale per call. */
  todos: TodoItem[];
  /** optional syntax guardrail: if set, write-class tools refuse to persist a broken result. */
  lint?: (path: string, content: string) => string | null;
  /** optional PDF text extraction (node hosts wire a pdftotext-backed impl); absent => Read explains. */
  pdfText?: (path: string) => Promise<string>;
  /** optional model handle for tools that run their own LLM pass (e.g. Review, a self-critique).
   *  Populated by the Agent from its own ai/model; absent => such tools degrade to a no-op. */
  ai?: ChatLike;
  model?: string;
  /** optional sandbox background-job registry; enables `bash({background:true})`. Absent => no backgrounding. */
  jobs?: JobRegistry;
  /** The Agent's job registry, always present under an Agent (independent of `backgroundJobs`): lets a tool that
   *  outlives its soft foreground timeout (an MCP call) hand itself off to a tracked job instead of failing. */
  toolJobs?: JobRegistry;
  /** optional incremental-output channel: long-running tools (e.g. the real Shell) stream chunks
   *  here mid-run. Wired PER CALL by Agent.dispatch to Hooks.onToolOutput (cleared when the call
   *  settles — a late emit is a silent no-op). Fire-and-forget: never awaited, never part of the result. */
  emit?: (chunk: string) => void;
  /** Wrap a HUMAN-blocking await (permission/plan confirm, an interactive question) so the time
   *  spent parked on the user is excluded from the run's wall-clock kill-switches. Wired by the Agent;
   *  absent => no accounting (the promise is awaited as-is). Idle prompt time must not count as work. */
  parkHuman?<T>(p: Promise<T>): Promise<T>;
  /** Set by `AskUserQuestion` when the model has a decision that is genuinely the user's to make but
   *  no human is reachable (headless/piped/edge). The Agent drains it after the tool batch and ends
   *  the run with `finishReason:'needs_input'`, handing the question to the caller instead of letting
   *  the agent guess. Cleared at the start of every run. */
  needsInput?: UserQuestion;
}

export interface AgentTool {
  name: string;
  description: string;
  parameters: object; // JSON Schema for the function's arguments
  /**
   * Longest wall-clock time this tool can legitimately take before it returns on its own.
   * Declared so a TRANSPORT above the loop (e.g. the cursor helper's per-call watchdog) can derive a
   * deadline with real headroom instead of carrying an independently-maintained magic number that
   * drifts into a tie with the tool's own cap — a tie the transport wins, destroying the tool's real
   * result. Omit for tools that return promptly.
   */
  maxDurationMs?: number;
  /**
   * Is `maxDurationMs` SELF-ENFORCED — does this tool abort itself at that bound and return?
   * An enforced bound cannot be overrun, so a transport above it needs only modest slack; an ADVISORY
   * one (the default) may be exceeded, so the transport keeps generous headroom over it. Declaring a
   * bound you do not actually enforce is worse than declaring none — say `true` only when you abort.
   */
  maxDurationEnforced?: boolean;
  run(args: any, ctx: ToolContext): Promise<string | { text: string; images?: { mimeType: string; data: string }[] }>;
  /** Optional: return a copy of this tool bound to a different real-disk working directory.
   *  Implemented by tools that escape the VFS (the real `Shell`), which are cwd-bound at construction
   *  and would otherwise keep pointing at the PARENT's tree when the tool instance is inherited by an
   *  isolated child agent. `subagent.ts` calls this for a worktree child; tools without it pass through.
   *  Return `undefined` for a capability that CANNOT follow the child (the background-job companions,
   *  bound to the parent's job registry) — the tool is then dropped from the child's toolbelt.
   *  An implementation must carry over any post-construction mutation the host made to the instance
   *  (a renamed `name`/`description`) — rebuilding from the factory's options alone loses it. */
  withCwd?(cwd: string): AgentTool | undefined;
}

/** Build a tool context bound to a filesystem backend (Mem / Disk / …) and an optional host. */
export function makeContext(fs: IFilesystem, host?: HostBridge): ToolContext {
  const exec = new CommandExecutor(fs);
  registerHeadlessCommands(exec);
  return { fs, exec, readState: new Map(), host, todos: [] };
}

/** Convert AgentTools into the ai.libx.js `tools` array for chat(). */
export function toWireTools(tools: AgentTool[]): Tool[] {
  return tools.map((t) => ({
    type: 'function',
    function: { name: t.name, description: t.description, parameters: t.parameters },
  }));
}

const numberLines = (content: string, offset = 0, limit?: number): string => {
  const lines = content.split('\n');
  const start = Math.max(0, offset);
  const end = limit != null ? start + limit : lines.length;
  return lines
    .slice(start, end)
    .map((l, i) => `${start + i + 1}\t${l}`)
    .join('\n');
};

/** Keep huge tool output high-signal: head+tail with an omission marker (cuts re-runs to "parse the wall"). */
export function truncateOutput(s: string, headLines = 80, tailLines = 20): string {
  const lines = s.split('\n');
  if (lines.length <= headLines + tailLines + 1) return s;
  const omitted = lines.length - headLines - tailLines;
  return [...lines.slice(0, headLines), `… (${omitted} lines omitted — narrow the command to see more) …`, ...lines.slice(-tailLines)].join('\n');
}

/** Run any shell command line over the VFS (ls/cat/grep/find/head/tail/echo/mkdir/rm/mv/wc, pipes, redirects, &&/||/;). */
export const bashTool: AgentTool = {
  name: 'bash',
  description:
    'Run a shell command. Supports ls, cat, grep, find, head, tail, echo, mkdir, rm, mv, cp, wc, pipes (|), redirects (>, >>), and chaining (&&, ||, ;). Best for: running tests/builds, file operations (mkdir/mv/rm), and piped workflows. For searching file contents, prefer `Grep` (structured results, no re-parse). For finding files by name, prefer `Glob`.',
  parameters: {
    type: 'object',
    required: ['command'],
    properties: {
      command: { type: 'string', description: 'the command line to execute' },
      background: { type: 'boolean', description: 'run detached over an isolated overlay (writes commit when it finishes); returns a job id to poll with JobOutput. Only worth it for slow work (remote VFS / long pipelines).' },
    },
  },
  async run({ command, background }, ctx) {
    if (background && ctx.jobs) return startBashJob(String(command ?? ''), ctx);
    const r = await ctx.exec.execute(String(command ?? ''));
    const out = truncateOutput((r.output ?? '').replace(/\n+$/, ''));
    if (r.exitCode !== 0) {
      const err = (r.error ?? '').trim();
      return `[exit ${r.exitCode}]${err ? ' ' + err : ''}${out ? '\n' + out : ''}`;
    }
    return out || '(command succeeded, no output)'; // explicit sentinel: don't re-run to "check"
  },
};

/** Kick a bash command into the background over an isolated overlay; its writes commit only on success.
 *  A kill (abort) before completion skips the commit — the parent VFS is never touched mid-flight. */
function startBashJob(command: string, ctx: ToolContext): string {
  const baseFs = ctx.fs;
  const id = ctx.jobs!.start(
    async ({ signal }) => {
      const overlay = new OverlayFilesystem(baseFs);
      const exec = new CommandExecutor(overlay);
      registerHeadlessCommands(exec);
      const r = await exec.execute(command); // wcli is sync-to-completion; abort can only gate the commit below
      if (signal.aborted) return '[killed before commit]';
      await overlay.commit(); // atomically flush this job's writes down into the parent VFS
      const out = truncateOutput((r.output ?? '').replace(/\n+$/, ''));
      return r.exitCode !== 0 ? `[exit ${r.exitCode}] ${(r.error ?? '').trim()}\n${out}`.trim() : out || '(command succeeded, no output)';
    },
    { kind: 'bash', label: command.slice(0, 60) },
  );
  return `Started background job ${id} — poll with JobOutput({id:"${id}"}) / JobStatus, stop with JobKill.`;
}

/** Image extensions the Read tool returns as a visual block (when the fs can read bytes). */
/** The real on-disk path behind a VFS path, when the fs exposes one; null (with a reason) otherwise —
 *  callers then fall back to inlining the bytes. */
function realPathOf(fs: { realPath?: (p: string) => string }, path: string): string | null {
  if (typeof fs.realPath !== 'function') return null;
  try { return fs.realPath(path); }
  catch (e) { log.debug(`realPath unavailable for ${path} — inlining image bytes instead`, e); return null; }
}

const IMG_MIME: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };

/** Heuristic: is this utf8-decoded string actually a binary file? A NUL byte never occurs in text; a
 *  dense run of U+FFFD replacement chars means the bytes weren't valid utf8. Cheap — scans a prefix. */
function isBinary(raw: string): boolean {
  if (raw === '') return false; // empty is handled separately (could be a legit 0-byte text file)
  const head = raw.length > 8192 ? raw.slice(0, 8192) : raw;
  let repl = 0;
  for (let i = 0; i < head.length; i++) {
    const c = head.charCodeAt(i);
    if (c === 0x00) return true; // NUL byte -> definitively binary
    if (c === 0xfffd) repl++; // utf8 decode failure marker
  }
  return repl / head.length > 0.1;
}

/** Byte length of a file — exact via readFileBytes when the fs exposes it, else utf8 byte estimate. */
async function byteSize(fs: unknown, path: string, raw: string): Promise<number> {
  const bfs = fs as { readFileBytes?: (p: string) => Promise<Uint8Array> };
  if (typeof bfs.readFileBytes === 'function') {
    try { return (await bfs.readFileBytes(path)).length; } catch { /* fall through to estimate */ }
  }
  return Buffer.byteLength(raw, 'utf8');
}

/** Read a text file as 1-indexed numbered lines; arms the staleness guard for Edit. */
export const readTool: AgentTool = {
  name: 'Read',
  description:
    'Read a file. Text files return 1-indexed numbered lines (with optional `offset`/`limit` and a re-Read pointer for partial reads). Image files (png/jpg/jpeg/gif/webp) return the picture itself so you can SEE it. PDFs return their extracted text. Always Read a file before Editing it.',
  parameters: {
    type: 'object',
    required: ['path'],
    properties: {
      path: { type: 'string' },
      offset: { type: 'number' },
      limit: { type: 'number' },
    },
  },
  async run({ path, offset, limit }, ctx) {
    // Image file → return it as a visual block. The adapter turns a tool result whose JSON carries a
    // `dataUrl` into an image tool_result the model can see. Needs a binary-capable fs (disk default);
    // the utf8 VFS (sandbox/Mem) can't, so we say so instead of corrupting the bytes.
    const ext = String(path).toLowerCase().split('.').pop() ?? '';
    // PDF → extracted text when the host wired an extractor (CLI: pdftotext); else say how to proceed.
    if (ext === 'pdf') {
      if (!ctx.pdfText) return `[${path} is a PDF — text extraction isn't available in this environment (install poppler's pdftotext and run on disk).]`;
      if (!(await ctx.fs.exists(path))) return `Error: File not found: ${path}`; // jail-aware: hidden paths read as absent
      const text = (await ctx.pdfText(ctx.fs.resolvePath(path))).trim();
      return text ? numberLines(text, Math.max(0, offset ?? 0), limit) : `[${path}: no extractable text (scanned/image-only PDF?)]`;
    }
    if (IMG_MIME[ext]) {
      const fs = ctx.fs as { readFileBytes?: (p: string) => Promise<Uint8Array>; realPath?: (p: string) => string };
      if (typeof fs.readFileBytes !== 'function') {
        return `[${path} is an image, but this filesystem can't read binary — attach it as @${path} instead, or run on disk.]`;
      }
      // Persist a REFERENCE, not the payload: the send path (expandImagesForSend) turns it back into the
      // same `{dataUrl}` body the wire adapter renders, for the recent images only — so a screenshot Read
      // costs ~80 bytes in the transcript/session JSON instead of megabytes of immortal base64.
      const real = realPathOf(fs, path);
      if (real) {
        if (!(await ctx.fs.exists(path))) return `Error: File not found: ${path}`;
        return imageRefResult(real, IMG_MIME[ext]);
      }
      const bytes = await fs.readFileBytes(path);
      const b64 = Buffer.from(bytes).toString('base64');
      return JSON.stringify({ dataUrl: `data:${IMG_MIME[ext]};base64,${b64}`, image: path });
    }
    const raw = await ctx.fs.readFile(path);
    // Binary guard: a non-text file (a .png without a recognized ext, .zip, .bin, …) read as utf8 is
    // either empty or replacement-char garbage — both silently pollute context. A silent empty tool
    // result is plausibly what tips a delegated model (cursor/composer) into an empty reply, where a
    // native model tolerates it. Return an explicit, non-empty marker so the model always sees SOMETHING.
    if (isBinary(raw)) {
      const bytes = await byteSize(ctx.fs, path, raw);
      return `[binary file: ${path} — ${bytes} bytes, not shown as text. Read renders png/jpg/jpeg/gif/webp images directly; otherwise attach it as @${path}.]`;
    }
    ctx.readState.set(ctx.fs.resolvePath(path), raw); // staleness guard tracks the REAL content
    if (raw === '') return `[empty file: ${path} — 0 bytes]`;
    // Mask secret values in config files so keys can live there usably-but-hidden (line count is preserved).
    const content = CONFIG_FILE_RE.test(ctx.fs.resolvePath(path)) ? redactSecrets(raw) : raw;
    const total = content === '' ? 0 : content.split('\n').length;
    const start = Math.max(0, offset ?? 0);
    const body = numberLines(content, start, limit);
    // snippet-with-pointer: when the slice doesn't cover the whole file, tell the
    // model what it's missing + how to pull it — so it expands precisely instead of re-reading blind.
    const shownEnd = limit != null ? Math.min(start + limit, total) : total;
    const shownCount = Math.max(0, shownEnd - start);
    if (shownCount >= total) return body; // whole file shown — no footer
    if (shownCount === 0) return `[no lines in range (offset ${start}${limit != null ? `, limit ${limit}` : ''}) — file has ${total} line(s)]`;
    return `${body}\n\n[lines ${start + 1}–${shownEnd} of ${total} · re-Read with offset/limit for the rest]`;
  },
};

/** Replace an exact, unique substring; requires a prior Read and guards against stale edits. */
export const editTool: AgentTool = {
  name: 'Edit',
  description:
    'Replace an exact substring in a file. Requires a prior Read of the same file. `old_string` must occur exactly once — include surrounding context to disambiguate.',
  parameters: {
    type: 'object',
    required: ['path', 'old_string', 'new_string'],
    properties: {
      path: { type: 'string' },
      old_string: { type: 'string' },
      new_string: { type: 'string' },
    },
  },
  async run({ path, old_string, new_string }, ctx) {
    const key = ctx.fs.resolvePath(path);
    const snapshot = ctx.readState.get(key);
    if (snapshot == null) throw new Error(`File has not been read yet: ${path}. Read it before editing.`);
    const current = await ctx.fs.readFile(path);
    if (current !== snapshot) throw new Error(`File ${path} changed since it was read (stale). Re-read before editing.`);
    const count = old_string === '' ? 0 : current.split(old_string).length - 1;
    if (count > 1) throw new Error(`old_string is not unique in ${path} (${count} matches). Provide more surrounding context.`);
      let next: string, note = '';
    if (count === 1) {
      next = current.replace(old_string, () => new_string); // exact: function replacer, no $-pattern expansion
    } else {
      // exact match failed — try a whitespace-tolerant unique match before giving up (cuts re-read churn)
      const fuzzy = fuzzyLineReplace(current, old_string, new_string);
      if (fuzzy == null) throw new Error(`old_string not found in ${path}.`);
      next = fuzzy;
      note = ' (whitespace-tolerant match)';
    }
    if (ctx.lint) { const err = ctx.lint(path, next); if (err) throw new Error(err); }
    await ctx.fs.writeFile(path, next);
    ctx.readState.set(key, next);
    return `Edited ${path}${note}`;
  },
};

/** Session-exit tool: the model calls this when the user wants to end the conversation.
 *  The `onExit` callback is injected by the host (CLI sets it to flip a flag that breaks the REPL loop). */
export function exitSessionTool(onExit: () => void): AgentTool {
  return {
    name: 'ExitSession',
    description:
      'End the current session and exit the CLI. Call this when the user says goodbye, asks to quit, ' +
      'or clearly indicates they want to stop the conversation (e.g. "ok bye", "that\'s all", "exit", "goodnight").',
    parameters: { type: 'object', properties: {} },
    async run() {
      onExit();
      return 'Session ending. Goodbye!';
    },
  };
}

export function defaultTools(): AgentTool[] {
  return [bashTool, readTool, editTool];
}

/**
 * The full catalog of selectable tools, keyed by name. The evolve loop's mutation
 * surface picks from this registry; embedders can build a custom tool set by name.
 */
export function toolRegistry(): Record<string, AgentTool> {
  const all = [bashTool, readTool, editTool, grepTool, globTool, writeTool, multiEditTool, applyEditsTool, repoMapTool, reviewTool(), todoWriteTool, webFetchTool, webSearchTool, webSearchAnthropicTool];
  return Object.fromEntries(all.map((t) => [t.name, t]));
}

/** Resolve a list of tool names against `available` (host-supplied tools, e.g. a parent agent's
 *  toolbelt or mounted MCP tools) first, then the built-in registry. Unknown names throw.
 *  Without `available` an allowlist could only ever name a built-in — so a def could not scope a
 *  child to a tool its parent injected (`Bash`, `ToolSearch`, …). */
export function toolsByName(names: string[], available?: AgentTool[]): AgentTool[] {
  const reg = toolRegistry();
  const extra = Object.fromEntries((available ?? []).map((t) => [t.name, t]));
  return names.map((n) => {
    const t = extra[n] ?? reg[n];
    if (!t) throw new Error(`unknown tool '${n}'. Known: ${[...new Set([...Object.keys(extra), ...Object.keys(reg)])].join(', ')}`);
    return t;
  });
}
